/* eslint-disable @typescript-eslint/no-explicit-any */
import { cn } from "@/lib/utils";
import apiRoutes from "@/src/apiRoutes";
import { Button } from "@/src/components/Button/Button";
import InputComponent from "@/src/components/Input/Input";
import AppendInput from "@/src/components/PhoneInput/AppendInput";
import { Label } from "@/src/components/Text/Text";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/src/components/shad/select";
import useUrlState from "@/src/hooks/use-url-state";
import { useCountries } from "@/src/hooks/useCountries";
import { usePaymentMethods } from "@/src/hooks/usePaymentMethods";
import api from "@/src/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@components/shad/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/shad/popover";
import _, { isEmpty } from "lodash";
import { useAppSelector } from "@/src/app/hooks";
import { Alert } from "@/src/components/Alert/Alert";
import { useTranslation } from "react-i18next";
// import { Tabs, TabsList, TabsTrigger } from "@/src/components/shad/tabs";

interface Props {
  currency?: string;
  allow_debit?: boolean;
  allow_credit?: boolean;
  country_id?: number;
}

const AddPayments = ({ country_id, allow_debit, allow_credit }: Props) => {
  const [, setAddPayment] = useUrlState("add_payment");
  const { t } = useTranslation();
  // const trans = (key: string) => t(`app.paymentMethods.${key}`);

  const formT = (key: string) =>
    t(`app.buysell.paymentMethods.addNewPayment.modal.forms.${key}`);
  // const [tab, setTab] = useState<string>("debit");
  const {
    register,
    getValues,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    mode: "onBlur",
  });
  const { user } = useAppSelector((state) => state?.user);
  const [provider, setProvider] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const { paymentMethods, isLoading } = usePaymentMethods({
    country_id,
    allow_credit,
    allow_debit,
    // payment_type: tab,
  });
  const queryClient = useQueryClient();
  const selectedProvider = paymentMethods?.find(
    (item) => item?.name === provider
  );
  const foundFields: any = selectedProvider?.fields;
  const { countries } = useCountries();

  const addPayment = async () => {
    const fieldsToPass = Object.fromEntries(
      foundFields?.map((filed: any) => [
        `${filed.field}`,
        filed?.type === "phone"
          ? `+${countryCode}${getValues(filed?.field)}`
          : getValues(filed?.field),
      ])
    );
    const res = await api.post(apiRoutes.addPaymentMethod, {
      type: "add",
      payment_id: selectedProvider?.id.toString(),
      payment_type: !_.isEmpty(watch("payment_type"))
        ? watch("payment_type")
        : selectedProvider?.allow_credit && selectedProvider?.allow_debit
        ? "both"
        : selectedProvider?.allow_credit
        ? "credit"
        : "debit",
      fields: fieldsToPass,
    });
    return res.data;
  };

  const { mutate, isLoading: mutateLoad } = useMutation({
    mutationKey: ["add_payment"],
    mutationFn: addPayment,
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(data?.info);
        setAddPayment(undefined);
        queryClient.invalidateQueries({ queryKey: ["user_payment_methods"] });
      } else {
        toast.warning(data?.info);
      }
    },
  });

  const submit: SubmitHandler<any> = () => {
    mutate();
  };

  console.log(watch());

  const selectedCountry =
    countries?.info.find((item) => item?.short_code_iso3 === countryCode) ??
    countries?.info.find((item) => item?.name === user?.country);
  useEffect(() => {
    if (selectedCountry) {
      setCountryCode(selectedCountry?.short_code_iso3);
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedProvider?.allow_credit && selectedProvider?.allow_debit) {
      setValue("payment_type", "debit");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvider]);
  if (isLoading) return null;

  if (isEmpty(paymentMethods))
    return (
      <div className="flex items-center justify-center">
        <span className="text-lg font-medium">
          We are not supported in this country yet 😔
        </span>
      </div>
    );

  return (
    <form onSubmit={handleSubmit(submit)} className="grid gap-2">
      <div className="grid gap-1">
        <Label>{formT("provider.label")}</Label>
        <Select onValueChange={setProvider}>
          <SelectTrigger>
            <SelectValue placeholder={formT("provider.placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {paymentMethods?.map((item) => (
              <SelectItem key={item?.id} value={item?.name}>
                {item?.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {(selectedProvider?.type === "Bank" ||
        (selectedProvider?.allow_credit && !selectedProvider?.allow_debit)) && (
        <div>
          <Alert variant={"warning"}>{formT("bankWarning")}</Alert>
        </div>
      )}
      {selectedProvider?.type !== "Bank" &&
        !selectedProvider?.allow_credit &&
        selectedProvider?.allow_debit && (
          <div>
            <Alert variant={"warning"}>
              This payment method can only be used for buy trades
            </Alert>
          </div>
        )}
      {selectedProvider?.allow_debit && selectedProvider?.allow_credit && (
        <div>
          {/* <Tabs
            defaultValue="debit"
            className="w-full"
            onValueChange={(val) => setValue("payment_type", val)}
          >
            <TabsList className="flex w-full">
              <TabsTrigger className="flex-1" value="debit">
                Buy
              </TabsTrigger>
              <TabsTrigger className="flex-1" value="credit">
                Sell
              </TabsTrigger>
              <TabsTrigger className="flex-1" value="both">
                Both
              </TabsTrigger>
            </TabsList>
          </Tabs> */}
          <Label>Payment Type</Label>
          <Select>
            <SelectTrigger>
              <SelectValue placeholder="Select payment type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="debit">Buy</SelectItem>
              <SelectItem value="credit">Sell</SelectItem>
              <SelectItem value="both">Both (Buy & Sell)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      {/* <div>
        {selectedProvider && (
          <div>
            {selectedProvider?.allow_credit ? (
              <span
                className={cn(
                  textVariants({ size: "xs", variant: "desc" }),
                  "flex items-center"
                )}
              >
                <Check className="text-green-500 h-4 w-4" />

                Selling is allowed
              </span>
            ) : (
              <span
                className={cn(
                  textVariants({ size: "xs", variant: "desc" }),
                  "flex items-center"
                )}
              >
                <X className="text-red-500 h-4 w-4" />

                Selling is not allowed
              </span>
            )}

            {selectedProvider?.allow_debit ? (
              <span
                className={cn(
                  textVariants({ size: "xs", variant: "desc" }),
                  "flex items-center"
                )}
              >
                <Check className="text-green-500 h-4 w-4" />

                Buying is allowed
              </span>
            ) : (
              <span
                className={cn(
                  textVariants({ size: "xs", variant: "desc" }),
                  "flex items-center"
                )}
              >
                <X className="text-red-500 h-4 w-4" />

                Buying is not allowed
              </span>
            )}
          </div>
        )}
      </div> */}
      {foundFields?.map((field: any, index: number) =>
        field?.type === "phone" ? (
          <AppendInput
            key={index}
            type={field?.type}
            label={field.label}
            {...register(field.field, { required: field.required })}
            errors={errors[field.field]?.message as string}
            selectOption={
              <Popover>
                <div className=" grid ">
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className={cn(
                        " flex justify-between text-black font-medium w-full items-center border-gray-200 bg-input-field rounded-md px-3 py-2.5 text-sm  shadow-none dark:bg-grey-500 dark:text-white whitespace-nowrap border-0"
                      )}
                    >
                      {selectedCountry ? (
                        <>
                          <img
                            src={selectedCountry?.image}
                            alt=""
                            className="h-4 w-4"
                          />
                          +{selectedCountry?.short_code_iso3}
                        </>
                      ) : (
                        <span>Eg. +233</span>
                      )}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50 " />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className={cn(
                      ` max-h-[--radix-popover-content-available-height] overflow-y-scroll p-0 min-w-[var(--radix-popover-trigger-width)]  z-[100] `
                    )}
                    align="start"
                  >
                    <Command
                    // defaultValue={
                    //   // countries?.info.find(
                    //   //   (item) => item?.name === user?.country
                    //   // )?.short_code_iso3
                    //   "233"
                    // }
                    >
                      <CommandInput placeholder={`Search country`} />
                      <CommandEmpty>Nothing found.</CommandEmpty>
                      <CommandGroup>
                        {countries?.info.map((item, index) => (
                          <CommandItem
                            key={index}
                            onSelect={() => {
                              setCountryCode(item?.short_code_iso3);
                            }}
                            value={item?.name + item?.short_code_iso3}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                countryCode === item?.short_code_iso3
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />

                            <div className="flex items-center h-1">
                              <span className="text-lg mr-2">
                                <img
                                  src={item?.image}
                                  alt=""
                                  className="h-4 w-4"
                                />
                              </span>
                              <span>
                                {item?.name} (+{item?.short_code_iso3})
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                  {/* {errors && (
                                  <span className="text-red-500 text-xs font-medium">{errors.}</span>
                              )} */}
                </div>
              </Popover>
            }
          />
        ) : field?.type === "select" ? (
          <div>
            <Label>{field.label}</Label>
            <Select onValueChange={(val) => setValue(field?.field, val)}>
              <SelectTrigger>
                <SelectValue placeholder={`Select ${field.label}`} />
              </SelectTrigger>
              <SelectContent>
                {field?.options?.map((item: any, index: number) => (
                  <SelectItem key={index} value={item}>
                    {item}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <InputComponent
            key={index}
            type={field?.type}
            label={field.label}
            errors={errors[field.field]?.message as string}
            {...register(field.field, { required: field.required })}
          />
        )
      )}
      <Button
        type="submit"
        loading={mutateLoad}
        // onClick={submit}
        className="mt-5"
      >
        {formT("addbutton")}
      </Button>
    </form>
  );
};

export default AddPayments;
